import { useEffect } from 'react';

/**
 * Standalone-hub kiosk detection.
 *
 * The Raspberry-Pi kiosk launcher opens the app with `?kiosk=1`. This hook reads
 * that flag (persisting it for the browser session, since the URL params get
 * stripped), and tags `<body class="caro-kiosk">` so kiosk-only CSS — currently
 * hiding the mouse cursor on the touchscreen — applies.
 *
 * Deliberately keyed off the explicit launcher flag, NOT `Capacitor.getPlatform()`:
 * the kiosk is platform `web`, but so is a normal desktop browser (admins/testing)
 * — gating on `web` would wrongly hide the cursor there. With the `?kiosk=1` flag,
 * Android, iOS, AND desktop web are all left exactly as-is.
 */

const KIOSK_FLAG_KEY = 'caro.kiosk';

export function isKioskMode(): boolean {
  try {
    return window.sessionStorage.getItem(KIOSK_FLAG_KEY) === '1';
  } catch {
    return false;
  }
}

export function useKioskMode(): void {
  useEffect(() => {
    let kiosk = false;
    try {
      const params = new URLSearchParams(window.location.search);
      if (params.get('kiosk') === '1') {
        kiosk = true;
        try { window.sessionStorage.setItem(KIOSK_FLAG_KEY, '1'); } catch { /* storage disabled */ }
        // Strip the flag from the URL (mirrors deviceToken/hexnodeUDID handling).
        params.delete('kiosk');
        const qs = params.toString();
        window.history.replaceState(
          {},
          '',
          window.location.pathname + (qs ? `?${qs}` : '') + window.location.hash,
        );
      } else {
        kiosk = isKioskMode();
      }
    } catch {
      // URL/storage unavailable — treat as non-kiosk (leave everything as-is).
    }
    if (kiosk) {
      document.body.classList.add('caro-kiosk');
    }
  }, []);
}
